import { useParams } from "react-router-dom";
import { COMMENT_MODE } from "context/CustomFormContext";
import { useStores } from "stores";

import { ApiKeyFields, Comment, CommentForm, PrimaryFormNames, SecondaryFormNames, TertiaryFormNames } from "types";

import { ReleaseUrlParams } from "../types";

import { useFetchComments } from "./useFetchComments";
import { ReturnReleaseFormState, usePrimaryFormState } from "./usePrimaryFormState";

export interface TertiaryFormStateCbProps {
  secondaryFormName: SecondaryFormNames; // Name of the secondary form
  secondaryFormId: string; // Unique identifier of the secondary form
  tertiaryFormName: TertiaryFormNames; // Name of the tertiary form
  tertiaryFormId: string; // Unique identifier of the tertiary form
  updateCommentCb?: (tertiaryFormId: string, comment: CommentForm) => (comment: Comment) => Comment; // Custom callback to update a comment
  deleteCommentCb?: (tertiaryFormId: string, fieldName: ApiKeyFields) => (comment: Comment) => boolean; // Custom callback to delete a comment
}

/**
 * Hook for managing the state and comment logic of tertiary forms.
 *
 * This hook provides a comment callback for tertiary forms and integrates with the primary form state.
 *
 * @returns A function that takes tertiary form properties and returns the form's state.
 */
export const useTertiaryFormState = (): ((props: TertiaryFormStateCbProps) => ReturnReleaseFormState) => {
  const {
    revisedFormStore: {
      comments: { postTertiaryComment, patchTertiaryComment, deleteTertiaryComment }, // API methods for tertiary comments
    },
  } = useStores();

  const { updateCommentState } = useFetchComments(); // Hook to update comment state
  const { userId = null, releaseId } = useParams<ReleaseUrlParams>(); // Extract user and release identifiers from the URL

  const formState = usePrimaryFormState(); // Get the base state from the primary form

  /**
   * Comment callback function for tertiary forms.
   *
   * Handles comment operations (add, edit, delete) for tertiary forms.
   *
   * @param {TertiaryFormStateCbProps} props - Configuration for the tertiary form.
   * @returns A function to handle comments based on the provided mode.
   */
  const commentCallback =
    ({
      secondaryFormName,
      secondaryFormId,
      tertiaryFormName,
      tertiaryFormId,
      updateCommentCb,
      deleteCommentCb,
    }: TertiaryFormStateCbProps) =>
    async (comment: CommentForm, mode: COMMENT_MODE) => {
      if (!releaseId) return; // Ensure `releaseId` is available

      try {
        switch (mode) {
          case COMMENT_MODE.ADD:
            // Add a tertiary comment
            await postTertiaryComment(
              userId,
              PrimaryFormNames.Releases,
              releaseId,
              secondaryFormName,
              secondaryFormId,
              tertiaryFormName,
              tertiaryFormId,
              comment,
            );
            break;
          case COMMENT_MODE.EDIT:
            // Edit a tertiary comment with an optional update callback
            await patchTertiaryComment(
              userId,
              PrimaryFormNames.Releases,
              releaseId,
              secondaryFormName,
              secondaryFormId,
              tertiaryFormName,
              tertiaryFormId,
              comment.fieldName,
              comment,
              updateCommentCb?.(tertiaryFormId, comment), // Use custom callback if provided
            );
            break;
          case COMMENT_MODE.DELETE:
            // Delete a tertiary comment with an optional delete callback
            await deleteTertiaryComment(
              userId,
              PrimaryFormNames.Releases,
              releaseId,
              secondaryFormName,
              secondaryFormId,
              tertiaryFormName,
              tertiaryFormId,
              comment.fieldName,
              deleteCommentCb?.(tertiaryFormId, comment.fieldName), // Use custom callback if provided
            );
            break;
        }
        // Update the state of comments for the related secondary form
        await updateCommentState(secondaryFormName);
      } finally {
        null; // Placeholder for cleanup if needed
      }
    };

  /**
   * Returns the state for the tertiary form, including the comment callback.
   *
   * @param {TertiaryFormStateCbProps} callbackProps - Properties for the tertiary form.
   * @returns {ReturnReleaseFormState} The form state with the configured comment callback.
   */
  return (callbackProps) => {
    return {
      ...formState, // Extend the primary form state
      comments: {
        ...formState.comments,
        commentCallback: commentCallback(callbackProps), // Provide the tertiary comment callback
      },
    };
  };
};
